// ============================================================================
// useEnterpriseSections.ts — ЕДИНЫЙ источник «Разделы Предприятия» (SSoT — сервер,
// SRP). Используется меню «Пуск» (StartMenu), панелью «Разделы» (DrawerSections)
// и плитками (SectionTiles) — ОДИН запрос к серверу, общий кэш.
//
// РАЗДЕЛЫ — ДЕРЕВО (иерархия, как в 1С): `parentId` (родитель) + `kind`
// ('section' — обычный раздел, 'group' — группировка-папка). Возвращаются КОРНИ
// с вложенными `children`. Ветки без объектов и без детей отбрасываются
// (группа-папка нужна, только если в ней что-то есть).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';

export interface EnterpriseSectionObject {
  objectType: string;
  objectName: string;
  synonym?: string;
}

export interface EnterpriseSection {
  id: string;
  name: string;
  sortOrder?: number;
  /** Родитель (пусто = корень) */
  parentId: string;
  /** 'section' — обычный раздел, 'group' — группировка-папка */
  kind: string;
  objects: EnterpriseSectionObject[];
  children: EnterpriseSection[];
}

interface SectionsResult {
  sections: EnterpriseSection[];
  synonyms: Record<string, string>;
}

function sortSections(list: EnterpriseSection[]): EnterpriseSection[] {
  return [...list].sort((a, b) => {
    const d = (a.sortOrder || 0) - (b.sortOrder || 0);
    return d !== 0 ? d : a.name.localeCompare(b.name);
  });
}

/** Построить дерево из плоского списка и отбросить пустые ветки. */
function buildTree(flat: EnterpriseSection[]): EnterpriseSection[] {
  const byId = new Map<string, EnterpriseSection>();
  for (const n of flat) byId.set(n.id, { ...n, children: [] });
  const roots: EnterpriseSection[] = [];
  for (const n of byId.values()) {
    const parent = n.parentId ? byId.get(n.parentId) : undefined;
    if (parent) parent.children.push(n);
    else roots.push(n);
  }
  const prune = (n: EnterpriseSection): EnterpriseSection | null => {
    n.children = sortSections(n.children.map(prune).filter((c): c is EnterpriseSection => c !== null));
    return n.objects.length > 0 || n.children.length > 0 ? n : null;
  };
  return sortSections(roots.map(prune).filter((n): n is EnterpriseSection => n !== null));
}

async function loadSectionsFromServer(): Promise<SectionsResult> {
  const { getSDK } = await import('@/store/sdkStore');
  const { readThrough } = await import('@/lib/offlineCache');
  const sdk = getSDK();
  // ОФЛАЙН-ФЁРСТ: разделы и объекты разделов читаются «через кэш» — при обрыве
  // связи структура открывается из сохранённого (сервер — SSoT при связи).
  const r: any = (await readThrough('sections', () => sdk.section.listSections({}))).data;
  const raw = ((r as any)?.items || []).filter((s: any) => s.isVisible !== false);
  const flat: EnterpriseSection[] = await Promise.all(raw.map(async (s: any) => {
    let objects: EnterpriseSectionObject[] = [];
    try {
      const objs: any = (await readThrough(`section-objects:${s.id}`, () => sdk.section.listSectionObjects({ sectionId: s.id }))).data;
      objects = (objs.items || []).filter((o: any) => o.objectType && o.objectName);
    } catch {
      objects = [];
    }
    return {
      id: s.id,
      name: s.name,
      sortOrder: Number(s.sortOrder || s.order || 0),
      parentId: String(s.parentId || s.parent_id || ''),
      kind: String(s.kind || 'section'),
      objects,
      children: [],
    } as EnterpriseSection;
  }));
  const m: Record<string, string> = {};
  try {
    const [cats, docs, regs] = await Promise.all([
      sdk.metadata.listCatalogs({}), sdk.metadata.listDocuments({}), sdk.metadata.listRegisters({}),
    ]);
    for (const c of (cats as any).items || []) m[`catalog.${c.code}`] = c.synonym || c.name;
    for (const d of (docs as any).items || []) m[`document.${d.code}`] = d.synonym || d.name;
    for (const r2 of (regs as any).items || []) m[`register.${r2.code}`] = r2.synonym || r2.name;
  } catch { /* синонимы некритичны */ }
  return { sections: buildTree(flat), synonyms: m };
}

interface EnterpriseSectionsState {
  sections: EnterpriseSection[] | null;
  synonyms: Record<string, string>;
  /** Ошибка загрузки (сеть/сессия) — НЕ пусто «Разделы не настроены», а сбой */
  error: boolean;
}

/**
 * Разделы Предприятия (единый кэш, дерево). Пока не загружено — sections=null
 * (загрузка один раз при монтировании оболочки). error=true — сервер
 * недоступен/сессия: UI показывает сбой, а НЕ пустые разделы.
 * forceReload — повторная загрузка (после изменений в конфигураторе).
 */
export function useEnterpriseSections(): {
  sections: EnterpriseSection[] | null;
  synonyms: Record<string, string>;
  error: boolean;
  reload: () => void;
} {
  const [state, setState] = useState<EnterpriseSectionsState>({ sections: null, synonyms: {}, error: false });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // При повторной попытке сбрасываем error (грузим заново)
    if (tick > 0) setState((prev) => ({ ...prev, error: false }));
    void loadSectionsFromServer().then((res) => {
      if (!cancelled) setState({ sections: res.sections, synonyms: res.synonyms, error: false });
    }).catch(() => {
      if (!cancelled) setState((prev) => ({ sections: prev.sections, synonyms: prev.synonyms, error: true }));
    });
    return () => { cancelled = true; };
  }, [tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { sections: state.sections, synonyms: state.synonyms, error: state.error, reload };
}

/** Отображаемое имя объекта раздела (синоним → имя из метаданных → код). */
export function sectionObjectName(o: EnterpriseSectionObject, synonyms: Record<string, string>): string {
  return o.synonym || synonyms[`${o.objectType}.${o.objectName}`] || o.objectName;
}

/** Группировка-папка (ветка дерева), а не рабочий раздел. */
export function isSectionGroup(s: EnterpriseSection): boolean {
  return s.kind === 'group';
}

/** Плоский список всех узлов дерева (в порядке обхода) — для потребителей,
 *  которым нужен «все разделы» без обхода children (например, виджет-хаб). */
export function flattenSections(sections: EnterpriseSection[]): EnterpriseSection[] {
  const out: EnterpriseSection[] = [];
  const walk = (list: EnterpriseSection[]) => {
    for (const n of list) {
      out.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  };
  walk(sections);
  return out;
}

/**
 * Фильтр дерева по поисковой строке: раздел/группа остаётся, если совпало имя,
 * или совпал объект, или есть совпавший потомок.
 */
export function filterSectionTree(sections: EnterpriseSection[], query: string, synonyms: Record<string, string>): EnterpriseSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  const walk = (nodes: EnterpriseSection[]): EnterpriseSection[] => {
    const out: EnterpriseSection[] = [];
    for (const n of nodes) {
      const children = walk(n.children);
      const objects = n.objects.filter((o) => sectionObjectName(o, synonyms).toLowerCase().includes(q));
      if (children.length > 0 || objects.length > 0 || n.name.toLowerCase().includes(q)) {
        out.push({ ...n, children, objects });
      }
    }
    return out;
  };
  return walk(sections);
}
